import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import PropTypes from 'prop-types';
import { Button, Slider, makeStyles } from '@material-ui/core';
import AmericoCoverageLengths from "../../../Helpers/Lists/CoverageLengths/AmericoCoverageLengths";
import AmericoCoverageAmounts from "../../../Helpers/Lists/CoverageAmounts/AmericoCoverageAmounts";
import AmericoHealthClasses from "../../../Helpers/Lists/HealthClasses/AmericoHealthClasses";
import ErrorHandler from '../../../Helpers/Errors/ErrorHandler';
import NoResultsFoundError from '../../../Helpers/Errors/CustomErrors/NoResultsFoundError';
import QuoteResultsSection from './QuoterPanels/QuoteResultsSection';
import { TextInput, SelectInput } from '../../../Helpers/Inputs';
import { CollapsibleBasicInfoSummary } from '../../Common/SubComponents/BasicInfoSummary';
import NumberFormatDollars from '../../../Helpers/Formatters/NumberFormatDollars';
import ShowOtherOptions from "../../Common/SubComponents/ShowOtherOptions";
import { 
  SESSION_STORAGE_ACCOUNT_KEY, 
  SESSION_STORAGE_TERM_ALTERNATIVES_KEY, 
  getSessionStorageItem, 
  setSessionStorageItem, 
  SESSION_STORAGE_COVERAGE_AMOUNT_KEY, 
  SESSION_STORAGE_NEEDS_ANALYSIS_KEY, 
  SESSION_STORAGE_ACTIVE_PURL, 
  getBasicInfo, 
  setBasicInfo,
} from '../../../../sessionStorage';
import { getTermHealthHealthClass, getHealthClass } from '../Helpers/GetHealthClass';
import NumberFormat from "react-number-format";
import QuoteCardTermAlternative from "../../Common/Cards/QuoteCardTermAlternative";
import ProductInfoDialog from "../../Common/Cards/ProductInfoDialog";
import BasicInfoSummaryModel from '../../Common/SubComponents/BasicInfoSummaryModel';

import '../../Quoter.scss';
import '../Americo.scss';

const useStyles = makeStyles((theme) => ({
  header: {
    color: theme.palette.primary.main,
  },
}));

export default function Quoter(props) {
  const classes = useStyles();
  const covrServicesUrl = process.env.REACT_APP_COVR_SERVICES_URL;
  
  const isActivePurl = getSessionStorageItem(SESSION_STORAGE_ACTIVE_PURL).active;
  const basicInfo = getBasicInfo();

  const [loading, setLoading] = useState(true);
  const [termAlternatives, setTermAlternatives] = useState([]);
  const [productInfo, setProductInfo] = useState({ productDescription: '', productRange: { minFace: 0, maxFace: 0 } });
  const [productInfoDialogOpen, setProductInfoDialogOpen] = useState(false);
  const [americoTermAlternatives, setAmericoTermAlternatives] = useState([]);
  const [basicInfoModalShow, setBasicInfoModalShow] = useState(false);
  const [isPurlVerification, setIsPurlVerification] = useState(isActivePurl);
  const tobacco = basicInfo.tobacco !== 'Never' && basicInfo.tobacco !== 'false';

  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);

  const quoteResultsPanelFields = {
    coverageAmount: props.formState.coverageAmount,
    term: props.formState.term,
    healthClass: props.formState.healthClass,
    termAlternatives: termAlternatives,
    setTermAlternatives: setTermAlternatives
  };
  const hideAmericoTermAlternatives = true;
  const [quoteResults, setQuoteResults] = useState([]);

  const onNextApply = (quote) => {
    props.formState.carrier = quote.carrierName;
    props.formState.carrierLogoUrl = quote.carrierLogoUrl;
    props.formState.premium = quote.premium;
    props.onNextApply(quote.productID);
  };

  let debounce = null;
  const setQuoteTimer = () => {
    let isSubscribed = true;
    if (debounce) {
      clearTimeout(debounce);
    }

    debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);
    return () => {
      clearTimeout(debounce);
      isSubscribed = false;
    }
  }

  useEffect(() => {
    if (props.formState.coverageAmount === null || props.formState.healthClass === null || props.formState.term === null) {
      return;
    }
    return setQuoteTimer();
  }, [props.formState.coverageAmount, props.formState.healthClass, props.formState.term]);

  useEffect(() => {
    window.scroll(0, 0);
    let faceAmount = 300000;
    if (props.formState.coverageAmount === 0) {
      const coverageValue = sessionStorage.getItem(SESSION_STORAGE_NEEDS_ANALYSIS_KEY);
      if (coverageValue > 0) {
        faceAmount = coverageValue;
      };
      const newValue = sessionStorage.getItem(SESSION_STORAGE_COVERAGE_AMOUNT_KEY);
      if (newValue) {
        faceAmount = newValue;
      }
    } else {
      faceAmount = props.formState.coverageAmount;
    }

    props.onFieldChange('coverageAmount', faceAmount);
  }, []);

  const setQuoteAlternatives = (quoteTermAlternatives) => {
    setSessionStorageItem(SESSION_STORAGE_TERM_ALTERNATIVES_KEY, quoteTermAlternatives);
  };

  function runQuote() {
    setLoading(true);
    clearQuoteResults();
    let monthlyRate;
    let deathBenefitSchedule;
    let logoUrl;
    let ageDetermination;

    const basicInfo = getBasicInfo();

    fetch(`${covrServicesUrl}/api/Americo?faceAmount=${props.formState.coverageAmount}&term=${props.formState.term}&state=${basicInfo.state}&birthdate=${basicInfo.birthdate || basicInfo.fakeBirthdate}&healthClass=${getHealthClass(props.formState.healthClass)}&gender=${basicInfo.gender}&tobacco=${tobacco}`,
      {
        method: 'GET',
        headers: {
          'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId
        }
      })
      .then(res => res.json())
      .then(data => {
        if (!data || !data.deathBenefitSchedule) {
          throw new NoResultsFoundError();
        }
        setAmericoTermAlternatives(data);
        deathBenefitSchedule = data.deathBenefitSchedule;
        logoUrl = data.logoUrl;
        monthlyRate = data.monthlyRate;
        ageDetermination = data.ageDetermination;
        props.formState.cashValue = data.cashValue;
        props.formState.premium = monthlyRate;
      })
      .then((rates) => {
        if (!hideAmericoTermAlternatives) {
          const feet = basicInfo.height.split('ft')[0].trim();
          const inches = basicInfo.height.split('ft')[1].split('in')[0].trim();
          const weight = basicInfo.weight.split(' ')[0];

          const queryParams = {
            birthdate: basicInfo.birthdate,
            state: basicInfo.state,
            gender: basicInfo.gender,
            faceAmount: props.formState.coverageAmount,
            tobacco: tobacco,
            healthClass: getTermHealthHealthClass(props.formState.healthClass),
            term: props.formState.term,
            substandardRating: props.formState.healthClass === 'good' ? 'B' : '',
            heightFt: feet,
            heightIn: inches,
            weight: weight,
            oneClickAccountId: accountObj.account.oneClickAccountId,
            replacePolicy: false,
            noKnockouts: true,
          };

          fetch(`${covrServicesUrl}/api/MCT/GetMCTQuote`,
            {
              method: 'POST',
              headers: {
                'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(queryParams)
            })
            .then(res => res.json())
            .then(data => {
              const quoteCardsData = [];

              if (data.length > 0) {

                const bestPrice = data.sort((a, b) => a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
                const bestPriceCardData = { ...bestPrice, isBestPrice: true, isMostOptions: false, isFastest: false };

                const fastest = data.sort((a, b) => b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
                const fastestCardData = { ...fastest, isFastest: true, isMostOptions: false, isBestPrice: false };

                const mostOptions = data.sort((a, b) => b.optionsScore - a.optionsScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
                const mostOptionsCardData = { ...mostOptions, isMostOptions: true, isFastest: false, isBestPrice: false };

                quoteCardsData.push(bestPriceCardData, fastestCardData, mostOptionsCardData);
              }

              setQuoteAlternatives(quoteCardsData);
              setTermAlternatives(quoteCardsData);
              setQuoteResults(
                <QuoteResultsSection
                  fields={quoteResultsPanelFields}
                  onNextApply={onNextApply}
                  onNext={onSubmit}
                  deathBenefitSchedule={deathBenefitSchedule}
                  monthlyRateAmerico={monthlyRate}
                  logoUrl={logoUrl}
                  termAlternatives={quoteCardsData}
                  loading={false}
                  ageDetermination={ageDetermination}
                />
              );
              setLoading(false);
            });
        }
        else {
          setQuoteResults(
            <QuoteResultsSection
              fields={quoteResultsPanelFields}
              onNextApply={onNextApply}
              onNext={onSubmit}
              deathBenefitSchedule={deathBenefitSchedule}
              monthlyRateAmerico={monthlyRate}
              logoUrl={logoUrl}
              termAlternatives={[]}
              loading={false}
              ageDetermination={ageDetermination}
            />
          );
          setLoading(false);
        }
      }).catch((error) => {
        ErrorHandler(error, setQuoteResults);
        setLoading(false);
      });
  }

  function clearQuoteResults() {
    setTermAlternatives([]);
    setAmericoTermAlternatives([]);
    setQuoteResults(
      <QuoteResultsSection
        fields={quoteResultsPanelFields}
        loading={true}
        onNextApply={onNextApply}
        onNext={onSubmit}
        termAlternatives={[]}
        deathBenefitSchedule={[]}
      />);
  }
  const termComparisonWithHealthClass = () => {
    if (props.formState.healthClass === 'best') {
      if (americoTermAlternatives.monthlyRate > termAlternatives[0].monthlyRate) {
        return 'Traditional term life insurance may be your best option';
      }
        return 'Compare to traditional term life insurance';

    }
      return 'Compare to traditional term life insurance';
  };

  const setProductInformation = (show, data) => {
    setProductInfo(data);
    setProductInfoDialogOpen(show);
  };

  const onSubmit = (e) => {
    if (e) e.preventDefault();
    const basicInfo = getBasicInfo();
    if (isActivePurl && !basicInfo.birthdate) {
      setIsPurlVerification(true);
      setBasicInfoModalShow(true);
    } else {
      props.onNext(e, props.monthlyRate);
    }
  }

  const handleBasicInfoSubmit = (open, purlMismatch) => {
    setBasicInfoModalShow(open);
    const basicInfo = getBasicInfo();
    setBasicInfo(basicInfo);
    if (isPurlVerification && !purlMismatch) {
      onSubmit();
    } else {
      setQuoteTimer();
    }
  }

  return (
    <Container>
      <Row className="quoter quoter-screen-reverse">
        <Col lg={4} className="mb-3">
          <Form className='form-data'>
            <h2>Get your quote</h2>
            <Form.Group>
              <TextInput
                className='coverage-data'
                name="coverageAmount"
                id="coverageAmount"
                labelAbove
                label="Select Coverage"
                value={props.formState.coverageAmount}
                onChange={(e) => props.onFieldChange('coverageAmount', e.target.value)}
                variant="outlined"
                InputProps={{
                  inputComponent: NumberFormatDollars,
                  className: "coverage-input",
                }}
              />
              <NumberFormat className="quote-amount" value={props.formState.coverageAmount} displayType={'text'} thousandSeparator={true} prefix='$' />
            </Form.Group>
            <Form.Group className="ml-3 mr-4">
              <Slider
                min={10000}
                max={1000000}
                step={null}
                value={parseInt(props.formState.coverageAmount, 10)}
                onChange={(e, v) => props.onFieldChange('coverageAmount', v)}
                className="m-2"
                marks={AmericoCoverageAmounts(1000000)}
              />
            </Form.Group>

            <Form.Group>

              <SelectInput
                name="term"
                id="term"
                value={props.formState.term}
                onChange={(e) => props.onFieldChange('term', e.target.value)}
                label="Years of Coverage"
                selectOptions={AmericoCoverageLengths()}
                hideEmptyOption
              />
            </Form.Group>

            <Form.Group>
              <SelectInput
                name="healthClass"
                id="healthClass"
                value={props.formState.healthClass}
                onChange={(e) => props.onFieldChange('healthClass', e.target.value)}
                label="Health Class"
                selectOptions={AmericoHealthClasses()}
                hideEmptyOption
              />
            </Form.Group>

            {/* <Form.Group>
              <SwitchInput
                name="wavePremiumRider"
                id="wavePremiumRider"
                label="Wave premium rider"
                checked={ConvertStringToBoolean(props.formState.wavePremiumRider)}
                onChange={(e) => props.onFieldChange('wavePremiumRider', ConvertBooleanToString(e.target.checked))}
                errors={errors.wavePremiumRider}
                inputRef={register}
                color="primary"
              />
              <Tooltip title="Premium Waiver Rider">
                <InfoIcon color="action"></InfoIcon>
              </Tooltip>
            </Form.Group> */}

            <CollapsibleBasicInfoSummary
              gender={basicInfo.gender}
              state={basicInfo.state}
              birthdate={basicInfo.birthdate || basicInfo.fakeBirthdate}
              tobacco={tobacco.toString()}
              height={basicInfo.height}
              weight={basicInfo.weight}
              showEditLink={true}
              editLinkOnClick={props.onBack}
            />
          </Form>
        </Col>

        <Col lg={8} className='responsiveQuoteFor'>
          {quoteResults}
        </Col>
      </Row>
      <Row className="quoter quoter-screen-reverse">
        <Col lg={4} />
        {
          !loading && termAlternatives.length === 0 &&
          <Col lg={8} className='quoter-data'>
            <div className="quote-underwriting-time text-center term-comparision-mobile">
              <div>
                Apply and buy in <span className={classes.header}>10-15 minutes</span>
                <div className='mt-3'>
                  <Button type="submit" variant="contained" color="primary" disabled={props.loading && !props.canContinue} onClick={(e) => onSubmit(e)}>Continue</Button>
                </div>
              </div>
            </div>
            <div className="container">
              {!hideAmericoTermAlternatives &&
                <div>
                  <h5 className="expando-title mt-4">{termComparisonWithHealthClass()}</h5>
                  <div className='row quote-vertical-card'>
                    {termAlternatives && termAlternatives.map((alt, index) =>
                      <QuoteCardTermAlternative
                        isBestPrice={alt.isBestPrice}
                        isFastest={alt.isFastest}
                        isMostOptions={alt.isMostOptions}
                        result={termAlternatives}
                        carrierName={alt.carrierName}
                        productName={alt.product}
                        annualRate={alt.annualRate}
                        monthlyRate={alt.monthlyRate}
                        logoUrl={alt.logoUrl}
                        rating={alt.rating}
                        productDescription={alt.productDescription}
                        quoteRanges={alt.productRange}
                        hasPermConversion={alt.hasPermConversionOption}
                        term={parseInt(alt.termLength, 10)}
                        key={index}
                        id={index}
                        expanded={props.expanded}
                        onNextApply={() => onNextApply(alt)}
                        setProductInfoDialogOpen={(show) => setProductInformation(show, alt)}
                        compareTo={americoTermAlternatives.monthlyRate}
                        requiresMedicalExam={alt.requiresMedicalExam}
                      />
                    )}
                  </div>
                </div>
              }
              <div>
                <ProductInfoDialog
                  open={productInfoDialogOpen}
                  onProductInfoDialogClosed={() => {
                    setProductInfoDialogOpen(false);
                  }}
                  productDescription={productInfo.productDescription}
                  quoteRanges={productInfo.productRange}
                  hasPermConversion={productInfo.hasPermConversionOption}
                  productName={productInfo.product}
                />
              </div>
              {!hideAmericoTermAlternatives &&
                <Row className='text-center'>
                  <ShowOtherOptions title='SHOW ALL TERM LIFE INSURANCE OPTIONS' healthClass={props.formState.healthClass} terms={props.formState.term} coverageAmount={props.formState.coverageAmount} termAlternatives={termAlternatives} />
                </Row>
              }
            </div>
          </Col>
        }
      </Row>
      {basicInfoModalShow && 
        <BasicInfoSummaryModel 
          openEditPopUp={basicInfoModalShow}
          closeModal={() => setBasicInfoModalShow(false)} 
          setOpenEditPopUp={handleBasicInfoSubmit}
          isPurlVerification={isPurlVerification}
          product='loanprotector'
          selectedQuote={americoTermAlternatives}
          onFieldChange={props.onFieldChange}
          formState={props.basicInfo} />}
    </Container>
  );
}

Quoter.propTypes = {
  onNext: PropTypes.func.isRequired,
  formState: PropTypes.object,
  basicInfo:PropTypes.object,
  loading:PropTypes.bool,
  onNextApply:PropTypes.func,
  onFieldChange:PropTypes.func,
  onBack:PropTypes.func,
  canContinue:PropTypes.bool,
  monthlyRate: PropTypes.number,
  expanded:PropTypes.bool
};

Quoter.DefaultFormState = (prefill = false, basicInfo = null, mctQuoterData = null) => {
  return {
    agreeToTerms: prefill ? true : false,
    coverageAmount: (mctQuoterData && mctQuoterData.coverageAmount) || (basicInfo && basicInfo.faceAmount) || 300000,
    healthClass: (mctQuoterData && mctQuoterData.healthClass) || (basicInfo && basicInfo.healthClass && basicInfo.healthClass.toLowerCase()) || '',
    term: (mctQuoterData && mctQuoterData.term) || (basicInfo && basicInfo.termLength) || "30",
    premium: (mctQuoterData && mctQuoterData.premium) || null,
    cashValue: (mctQuoterData && mctQuoterData.cashValue) || null,
    product: 'Americo',
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Americo/steps/Quoter.js